import {RESOURCE} from "../App/utils";
import {MyServices, VisualModels} from "../constants";
import {decryptKBItem} from "../App/cryptoUtils";
import {kbUserDataLoaded} from "../utils";

export const generateTransactionId = () => `${+new Date()}-${Math.floor(100000 + Math.random() * 900000)}`;

export const transactionSubjects = {
    paymentIntent: 'paymentIntent',
    kbId: 'kbId',
};

export const signKBTransactionJWT = async ({
    modelId, toAccountId, kbId, accountId, walletPrivateKey, walletPublicKey, message, maxAmount, unlimitedTransactions, expiresInSeconds}) => {
    kbId = kbId || kbUserDataLoaded?.kbId;

    const payload = {
        operation: "transfer",
        resourceId: RESOURCE.tokens,
        transactionId: generateTransactionId(),
        fromAccountId: accountId,
        fromAccountPublicKey: walletPublicKey,
        toAccountId: toAccountId || MyServices[modelId]?.accountId,
        message: message || '',
        subjectId: kbId ? kbId : undefined,
        subject: kbId ? transactionSubjects.kbId : undefined,
        maxAmount: maxAmount || 300000,
        iat: Math.round(+new Date()/1000),
    }

    if (unlimitedTransactions) payload.unlimitedTransactions = true;
    const transactionJWT = await signPayload(payload, accountId, walletPublicKey, decryptKBItem(walletPrivateKey), (expiresInSeconds || 1800));

    return transactionJWT
}

export async function signPayload(payload, accountId, publicKey, privateKey, expiresInSeconds = 1200) {
    // Check if publicKey hash matches accountId
    const encoder = new TextEncoder();
    const publicKeyBuffer = encoder.encode(publicKey);
    const hashBuffer = await crypto.subtle.digest('SHA-256', publicKeyBuffer);
    const hashHex = Array.from(new Uint8Array(hashBuffer)).map(b => b.toString(16).padStart(2, '0')).join('');
    if (hashHex.substring(0, 32) !== accountId) {
        throw `Public key does not belong to this accountId ${accountId}`;
    }

    // Define JWT header
    const header = {
        alg: 'ES256',
        typ: 'JWT'
    };

    // Encode header and payload to Base64Url
    const encodedHeader = base64urlEncode(JSON.stringify(header));
    const encodedPayload = base64urlEncode(JSON.stringify({...payload, exp: (Math.floor(Date.now() / 1000) + expiresInSeconds)}));

    // Import the private key for signing
    const ecPrivateKey = await crypto.subtle.importKey(
        'pkcs8',
        str2ab(atob(privateKey)), // Convert base64 encoded privateKey to ArrayBuffer
        {
            name: 'ECDSA',
            namedCurve: 'P-256', // Equivalent to ES256
        },
        false, // whether the key is extractable (i.e. can be used in exportKey)
        ['sign'] // can be "sign" or "verify" for asymmetric keys
    );

    // Prepare data to sign
    const data = encoder.encode(`${encodedHeader}.${encodedPayload}`);

    // Sign the data
    const signature = await crypto.subtle.sign(
        {
            name: 'ECDSA',
            hash: {name: 'SHA-256'}, // Specify hash function
        },
        ecPrivateKey,
        data // Data you want to sign
    );

    // Convert signature to Base64Url
    const encodedSignature = base64urlEncode(arrayBufferToString(signature));

    // Construct the JWT
    return `${encodedHeader}.${encodedPayload}.${encodedSignature}`;
}

// Helper function to convert string to ArrayBuffer
function str2ab(str) {
    const buf = new ArrayBuffer(str.length);
    const bufView = new Uint8Array(buf);
    for (let i = 0, strLen = str.length; i < strLen; i++) {
        bufView[i] = str.charCodeAt(i);
    }
    return buf;
}

// Helper function to convert ArrayBuffer to String
function arrayBufferToString(buffer) {
    return String.fromCharCode.apply(null, new Uint8Array(buffer));
}

// Helper function to Base64Url encode
function base64urlEncode(str) {
    return btoa(str)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
}

export async function encryptBigSecret(walletPrivateKey) {
    const chunkSize = 100;
    const chunks = Array.from({ length: Math.ceil(walletPrivateKey.length / chunkSize) }, (v, i) =>
        walletPrivateKey.slice(i * chunkSize, i * chunkSize + chunkSize)
    );

    // Encrypt each chunk
    return await Promise.all(chunks.map(chunk => encryptSecret(chunk)));
}


export async function encryptSecret(text) {
    // Functions public key
    const publicKeyPem = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAsB7YZD9GmrloeuApJAZV
kG7nPOj6vEXUa1iJFJ9+Xs5lFym47Tqc3s/X+GnIkF2VoNTp3AI3DY3pHFJLT5LR
5OMFrDe6qUU35Z/uZrB4RqSo1jdcqZld8/8Xz8F2fGOPe5yw1Ub5mAQO8Owng0MM
FYdpGlnW5YrXLMyp0YT6UbAYiHC44r1XJZovslxblOrHjzWiOj6On0iBtGK0X2oF
fZ7R/F8auBMIT4a7snDCWiugaEaI09LCPUC01ycnf0Zf1XCVTwI1wb91NAdRvpeN
qAvz3vUNAmxHt1zYiL+WMEYIMJ7YiNwr5cwJpBj+bZJjdmQr/r6+sjTiz7Me0GzM
QQIDAQAB
-----END PUBLIC KEY-----`;

    // Convert the PEM encoded public key to a format that the Web Crypto API can use
    const publicKey = await window.crypto.subtle.importKey('spki', pemToBuffer(publicKeyPem), {
            name: "RSA-OAEP",
            hash: "SHA-1",
        }, false, ["encrypt"]
    );

    // Convert the text to an ArrayBuffer
    const encoder = new TextEncoder();
    const data = encoder.encode(text);

    let encrypted;
    try {
        encrypted = await window.crypto.subtle.encrypt(
            {name: "RSA-OAEP"},
            publicKey,
            data
        );
        // Proceed with handling the encrypted data
    } catch (error) {
        console.error("Encryption failed:", error?.toString());
    }

    // Convert the encrypted data to a Base64 string
    return bufferToBase64(encrypted);
}

// Helper function to convert PEM encoded string to ArrayBuffer
function pemToBuffer(pem) {
    const base64String = pem
        .replace('-----BEGIN PUBLIC KEY-----', '')
        .replace('-----END PUBLIC KEY-----', '')
        .replace(/\s+/g, '');
    const binaryString = window.atob(base64String);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
}

// Helper function to convert ArrayBuffer to Base64 string
function bufferToBase64(buffer) {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}